/**
 * The app's processing floor: how long a "working…" state stays on screen.
 *
 * WHY A FLOOR EXISTS AT ALL
 * -------------------------
 * The simulated API resolves in about a second (SIMULATED_TIMINGS), and a real music
 * model will take considerably longer than that. Between those two numbers sits a UI
 * problem: a processing state that flashes past in about a second reads as a glitch
 * rather than as work, and the screen it reveals arrives before anyone has finished
 * reading the one they left. So the states that represent generation or analysis are held
 * for a minimum of PROCESSING_MS, and the result is revealed when both the work and that
 * minimum are done.
 *
 * WHAT THIS IS NOT
 * ----------------
 * It is not fake progress. Nothing here reports a percentage, a queue position or a
 * stage, because the API supplies none of those — see GenerationStatus for that
 * argument in full. It does not invent a result, delay an error, or claim that
 * something happened. The only thing being stretched is how long the user watches a
 * genuine wait: the request really is in flight, and if it takes longer than the floor
 * then it takes longer, because `withProcessingFloor` waits for whichever finishes last.
 *
 * The distinction matters for the honest reading of this file: remove the floor and the
 * app behaves identically except that the waiting state is shorter. No output changes.
 *
 * WHEN THE REAL API LANDS
 * -----------------------
 * A real generation will overrun this floor comfortably, at which point the floor stops
 * having any effect on generation and only still applies to the fast local operations
 * (a prompt reroll, a hashed analysis). That is the intended end state; nothing needs
 * removing.
 */

/**
 * How long a processing state is held, in milliseconds.
 *
 * THREE SECONDS, DOWN FROM FIVE, because the demo flow was reading as slow rather than as
 * busy. The floor was always the whole of the perceived wait — the simulated request
 * resolves in about 1.1s (SIMULATED_TIMINGS.generateSong) and `withProcessingFloor` waits
 * for whichever finishes last — so this constant, and only this constant, is what a user
 * times when they press Create. Nothing about the request, the job protocol or the poll
 * loop moved with it.
 *
 * IT IS STILL A FLOOR AND NOT A FAKE. Three seconds is comfortably longer than the 1.1s
 * request, so the state is still held rather than flashing, and the reasoning at the top of
 * this file is unchanged: no percentage, no stage, no invented result, and a rejection is
 * still not held back. Remove the floor entirely and the app behaves identically except
 * that the wait is shorter.
 *
 * It is one constant rather than a number typed into each screen, because "consistent
 * across Music DNA and Genre Mixer" is a requirement and two screens holding for 3s and
 * 2.5s would meet it by accident at best.
 */
export const PROCESSING_MS = 3000;

/** Resolves after `ms`. */
export function processingDelay(ms: number = PROCESSING_MS): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Runs `work` and resolves no sooner than `ms`, with whatever `work` produced.
 *
 * Promise.all rather than a measured start time and a computed remainder: the two race
 * to be last instead of one being subtracted from the other, so there is no clock to
 * read, nothing to get wrong when the work outruns the floor, and nothing for a fake
 * timer in a test to disagree with.
 *
 * A rejection is NOT held back. `Promise.all` settles on the first rejection, so a
 * failed request surfaces the moment it fails rather than sitting behind the rest of
 * the floor — which is the behaviour an error state wants: the one thing worse than a
 * wait is a wait that was already over.
 *
 * @param work The real operation. Started by the caller, so it is already in flight.
 * @param ms Minimum time the caller's processing state stays up.
 */
export async function withProcessingFloor<T>(
  work: Promise<T>,
  ms: number = PROCESSING_MS
): Promise<T> {
  const [result] = await Promise.all([work, processingDelay(ms)]);
  return result;
}
